import { Button, IconButton } from "@appretio/ui-kit";
import { useRef } from "react";
import {
  MAX_SCREENSHOTS,
  Screenshot
} from "../../hooks/useFeedbackScreenshots";
import * as styles from "./FeedbackScreenshotPicker.css";

/** Props for FeedbackScreenshotPicker. */
export interface FeedbackScreenshotPickerProps {
  /** Currently attached screenshots, in attach order. */
  screenshots: Screenshot[];
  /** Validation message from the most recent attach attempt, if any. */
  error: string | null;
  /** Whether the form is locked (a submit is in flight). */
  isDisabled: boolean;
  /** Called with the files the user chose in the file dialog. */
  onAddFiles: (files: File[]) => void;
  /** Called with the id of the screenshot the user detached. */
  onRemove: (id: number) => void;
}

/**
 * The screenshot-attachment area of the feedback modal: the picker button,
 * the hidden file input, the thumbnail strip with its remove buttons, and
 * the attach-validation message. Pasting is handled by the modal (a paste
 * anywhere inside it attaches), so this component owns no paste handler.
 */
export function FeedbackScreenshotPicker({
  screenshots,
  error,
  isDisabled,
  onAddFiles,
  onRemove
}: FeedbackScreenshotPickerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className={styles.screenshotSection}>
      <div className={styles.screenshotActions}>
        <Button
          type="button"
          size="sm"
          variant="tertiary"
          disabled={isDisabled}
          onClick={() => fileInputRef.current?.click()}
        >
          Attach image
        </Button>
        <span className={styles.screenshotHint}>
          or paste a screenshot (max {MAX_SCREENSHOTS})
        </span>
        <input
          ref={fileInputRef}
          className={styles.hiddenFileInput}
          type="file"
          accept="image/*"
          multiple
          disabled={isDisabled}
          aria-label="Attach screenshots"
          onChange={(event) => {
            onAddFiles(Array.from(event.target.files ?? []));
            event.target.value = "";
          }}
        />
      </div>
      {screenshots.length > 0 && (
        <div className={styles.thumbnailRow}>
          {screenshots.map((screenshot, index) => (
            <div key={screenshot.id} className={styles.thumbnail}>
              <img
                className={styles.thumbnailImage}
                src={screenshot.dataUri}
                alt={`Screenshot ${index + 1}`}
              />
              <span className={styles.thumbnailRemove}>
                <IconButton
                  iconName="close"
                  variant="secondary"
                  size="xs"
                  aria-label={`Remove screenshot ${index + 1}`}
                  disabled={isDisabled}
                  onClick={() => onRemove(screenshot.id)}
                />
              </span>
            </div>
          ))}
        </div>
      )}
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
}
